import { createContext, useContext, useEffect, useState } from "react";
import { getReseller, isDim } from "../utils";
import { useAuth } from "../auth/AuthContext";

export const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(false);
  const [loading, setLoading] = useState(true);
  const { activeCompany, loadingActiveCompany, loadingLogged, logged } =
    useAuth();

  const init = () => {
    const resellerCompany = getReseller(activeCompany);

    const palette = [
      {
        value: resellerCompany?.colours?.primary ?? "",
        cssVariable: "--color-leano-2",
      },
      {
        value: resellerCompany?.colours?.secondary ?? "",
        cssVariable: "--color-leano-3",
      },
      {
        value: resellerCompany?.colours?.tertiary ?? "",
        cssVariable: "--color-leano-4",
      },
    ];

    const applyMonoTheme = () => (document.body.className = "monoTheme");

    // Invert menu font color if needed
    const handleMenuColor = () => {
      const menuBackgroundColorInMonoTheme = "--color-leano-2"; // must be equal to definition in css
      const menuBackgroundColor = palette.find(
        (color) => color.cssVariable === menuBackgroundColorInMonoTheme,
      ).value;

      if (menuBackgroundColor) {
        if (isDim(menuBackgroundColor)) {
          document.body.style.setProperty("--menuColor", "white");
          document.body.style.setProperty("--menuNavColor", "white");
          document.body.style.setProperty(
            "--menuNavBackgroundColorLumPercentage",
            "10%",
          );
        } else {
          document.body.style.setProperty("--menuColor", "black");
          document.body.style.setProperty("--menuNavColor", "black");
          document.body.style.setProperty(
            "--menuNavBackgroundColorLumPercentage",
            "70%",
          );
        }
      }
    };

    // Invert menu font color if needed
    const handleNavMenuColor = () => {
      const menuNavBackgroundColorInMonoTheme = "--color-leano-3"; // must be equal to definition in css
      const menuNavBackgroundColor = palette.find(
        (color) => color.cssVariable === menuNavBackgroundColorInMonoTheme,
      ).value;

      if (menuNavBackgroundColor) {
        if (isDim(menuNavBackgroundColor)) {
          document.body.style.setProperty("--menuNavColor", "white");
        } else {
          document.body.style.setProperty("--menuNavColor", "black");
        }
      }
    };

    // Invert buttons font color if needed
    const handleButtonColor = () => {
      const primaryButtonBackgroundColorInMonoTheme = "--color-leano-2"; // must be equal to definition in css
      const primaryButtonBackgroundColor = palette.find(
        (color) =>
          color.cssVariable === primaryButtonBackgroundColorInMonoTheme,
      ).value;

      if (primaryButtonBackgroundColor) {
        if (isDim(primaryButtonBackgroundColor)) {
          document.body.style.setProperty("--primaryButtonColor", "white");
        } else {
          document.body.style.setProperty("--primaryButtonColor", "black");
        }
      }
    };

    const applyPalette = () => {
      for (const color of palette) {
        // Change color only if defined
        if (color.value) {
          document.body.style.setProperty(color.cssVariable, color.value);
        }
      }

      // Handle menu color
      handleMenuColor(palette);

      // Handle nav menu color
      handleNavMenuColor(palette);

      // Handle button color
      handleButtonColor(palette);
    };

    // Check if the active company is a reseller or one of its clients, and that at least one
    // custom field has been set; otherwise, use standard Leano theme.
    if (
      Object.values(resellerCompany?.colours || {}).some(Boolean) ||
      resellerCompany?.logo
    ) {
      setTheme(true);
      applyMonoTheme();
      applyPalette();
    }
  };

  useEffect(() => {
    setLoading(true);
    if (!loadingActiveCompany && !loadingLogged && logged) {
      init();
    }
    setLoading(false);
  }, [loadingActiveCompany]);

  const value = {
    theme,
    loading: loading || loadingActiveCompany,
  };

  return <ThemeContext value={value}>{!loading && children}</ThemeContext>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
